import {
  Factory,
  HardHat,
  Stethoscope,
  Users,
  ShoppingBag,
  Truck,
  Globe,
  Shirt,
  Briefcase,
  FileText,
  Handshake,
  BarChart3,
  BarChart2,
  Headphones,
  Hospital,
  FlaskConical,
  ClipboardList,
} from "lucide-react";
import { products } from "./products";

export type PlatformIcon =
  | "factory"
  | "hard-hat"
  | "stethoscope"
  | "users"
  | "shopping-bag"
  | "truck"
  | "globe"
  | "shirt"
  | "briefcase"
  | "file-text"
  | "handshake"
  | "bar-chart-3"
  | "bar-chart-2"
  | "headphones"
  | "hospital"
  | "flask-conical"
  | "clipboard-list";

export interface PlatformFeature {
  name: string;
  desc: string;
  href: string;
  icon: PlatformIcon;
}

export interface Platform {
  id: string;
  category: string;
  categoryShort: string;
  product: string;
  description: string;
  heroHeadline: string;
  heroSubheadline: string;
  color: string;
  gradient: string;
  gradientBg: string;
  icon: PlatformIcon;
  features: PlatformFeature[];

  productSlug?: string;

  painPoints?: { title: string; desc: string }[];
  industriesServed?: string[];
  aiFeatures?: {
    title: string;
    description: string;
    bulletPoints: string[];
  }[];
  processSteps?: { step: string; title: string; description: string }[];
  businessImpact?: {
    value: string;
    label: string;
    description: string;
    color: string;
    icon: string;
  }[];
  comparisonRows?: { feature: string; mxerp: string; others: string }[];
  faqItems?: { q: string; a: string }[];
  buyersChecklist?: string[];
}

const ICON_MAP: Record<PlatformIcon, React.ComponentType<{ className?: string }>> = {
  factory: Factory,
  "hard-hat": HardHat,
  stethoscope: Stethoscope,
  users: Users,
  "shopping-bag": ShoppingBag,
  truck: Truck,
  globe: Globe,
  shirt: Shirt,
  briefcase: Briefcase,
  "file-text": FileText,
  handshake: Handshake,
  "bar-chart-3": BarChart3,
  "bar-chart-2": BarChart2,
  headphones: Headphones,
  hospital: Hospital,
  "flask-conical": FlaskConical,
  "clipboard-list": ClipboardList,
};

export function resolvePlatformIcon(name: PlatformIcon) {
  return ICON_MAP[name];
}

const getRelated = (family: string) =>
  products.filter((p) => p.family === family);

export const platforms: Platform[] = [
  {
    id: "manufacturing-mx-erp",
    category: "Manufacturing & Trading",
    categoryShort: "Mfg & Trading",
    product: "Mx-ERP",
    description:
      "Enterprise-grade manufacturing execution with real-time shop floor control and advanced inventory management.",
    heroHeadline: "Next-Gen AI-Driven ERP Software to Scale Your Business Operations",
    heroSubheadline:
      "MX-ERP by ACGIL is a highly scalable, fully integrated AI-driven ERP Software designed to bring your finance, supply chain, production, and HR operations onto a single, secure cloud platform. Eliminate silos, leverage predictive analytics, and drive high profitability.",
    color: "#2C78B9",
    gradient: "from-[#2C78B9] to-[#059A8F]",
    gradientBg: "linear-gradient(135deg, #2C78B9 0%, #059A8F 100%)",
    icon: "factory",
    productSlug: "manufacturing-erp-software",
    painPoints: [
      { title: "Data Silos", desc: "Finance, inventory, and sales teams working on isolated spreadsheets, leading to mismatched numbers and reporting delays." },
      { title: "Inventory Losses", desc: "Lack of real-time inventory tracking causes either massive overstocking or sudden material stockouts." },
      { title: "Poor Cost Control", desc: "Difficulty in tracking exact project costs, manufacturing waste, or dynamic operational overheads." },
      { title: "Compliance Bottlenecks", desc: "Struggling with daily manual GST filings, GSTR reconciliations, and instant e-invoicing." },
      { title: "Manual Tracking Errors", desc: "Relying on physical papers or WhatsApp groups for purchase requisitions and manager approvals." },
    ],
    industriesServed: [
      "Automotive & Auto Components",
      "Food & Beverage Processing",
      "Electrical & Electronics",
      "Metal Fabrication & Engineering",
      "Chemical & Paint",
      "Packaging & Plastics",
      "Aerospace & Defence",
    ],
    aiFeatures: [
      {
        title: "Predictive Inventory Analytics",
        description: "AI algorithms analyze historic sales metrics to accurately project future demand trends, effectively slashing carrying costs.",
        bulletPoints: [
          "Analyzes historic sales data to predict demand",
          "Reduces carrying costs and overstocking",
          "Prevents sudden stockouts with smart alerts",
        ],
      },
      {
        title: "Automated Procurement Routing",
        description: "The system auto-evaluates dynamic market rates and vendor history to automatically recommend optimum purchase routing.",
        bulletPoints: [
          "Evaluates real-time market rates and vendor history",
          "Automatically recommends best procurement routes",
          "Reduces procurement lead times significantly",
        ],
      },
      {
        title: "Smart Financial Forecasting",
        description: "Machine learning tracks cash collection cycles to build accurate upcoming cashflow forecasts and flag delay risks early.",
        bulletPoints: [
          "ML-powered cash collection cycle tracking",
          "Accurate cashflow forecasts with risk flagging",
          "Enables proactive financial decision-making",
        ],
      },
    ],
    processSteps: [
      { step: "01", title: "Lead & Sales Management", description: "Log customer requirements, generate quotations, verify credit limits, and confirm sales orders with auto-stock checking." },
      { step: "02", title: "Procurement & Inventory Planning", description: "The engine runs material requirement calculations (MRP) based on minimum-maximum stocks and raises automated RFQs for vendors." },
      { step: "03", title: "Production & Project Scheduling", description: "Assign labor, schedule machines, allocate raw materials to specific jobs, and track live shop-floor progress." },
      { step: "04", title: "Quality Assurance Check", description: "Conduct rigorous inbound, in-process, and outbound quality checks to eliminate material rejections before final packaging." },
      { step: "05", title: "Automated Dispatch & Logistics", description: "Plan vehicle shipments, print barcodes, and auto-generate packing slips, delivery challans, and integrated e-way bills." },
      { step: "06", title: "Integrated Finance & Invoicing", description: "Directly push invoices to the GST portal (e-invoicing), auto-update general ledgers, and view instant department-wise P&L." },
    ],
    businessImpact: [
      { value: "32%", label: "Inventory Turnover", description: "Improvement in inventory turnover ratio with AI-driven stock optimization", color: "#00F2FE", icon: "TrendingUp" },
      { value: "25%", label: "Lower Overheads", description: "Reduction in operational overheads within year one of deployment", color: "#7F77DD", icon: "TrendingDown" },
      { value: "99.9%", label: "System Uptime", description: "Uptime with secure, hybrid, or cloud database options", color: "#F59E0B", icon: "Zap" },
      { value: "40%", label: "Faster Closures", description: "Faster monthly financial closing with automated workflows", color: "#EC4899", icon: "Activity" },
    ],
    comparisonRows: [
      { feature: "Technology Core", mxerp: "AI-Driven Automation & Predictive Analytics", others: "Static Manual Data Entry & Basic Reporting" },
      { feature: "Deployment Mode", mxerp: "Cloud, On-Premise, or Hybrid", others: "Cloud Only (Strict limits)" },
      { feature: "GST & Compliance", mxerp: "Built-in (E-invoice, GSTR Auto)", others: "External Add-on required" },
      { feature: "Customization Range", mxerp: "High (Configurable to your SOPs)", others: "Extremely Rigid & Expensive" },
      { feature: "Setup Timeline", mxerp: "6 to 8 Weeks", others: "6 to 12 Months" },
      { feature: "Local Support", mxerp: "24/7 Dedicated Account Manager", others: "Remote Helpdesk (Time-zone lags)" },
    ],
    faqItems: [
      { q: "What is an ERP Software and why does my business need it?", a: "An ERP (Enterprise Resource Planning) software integrates your entire business operations—including sales, inventory, finance, purchasing, production, and HR—into a single centralized database. It ensures real-time visibility, eliminates human data entry errors, and improves cash flow tracking." },
      { q: "Can we host MX-ERP on our local company servers?", a: "Yes. MX-ERP is highly flexible. We offer public cloud hosting (SaaS), private cloud hosting, and on-premise installation on your company's physical servers, depending on your data security preferences and infrastructure." },
      { q: "How much does a typical ERP Software implementation cost?", a: "The investment depends on your user licenses, the number of functional modules needed, and deployment choices. We offer custom, transparent pricing with no hidden licensing costs. You can speak to our solution expert for a custom quote." },
      { q: "Does MX-ERP support integration with existing platforms?", a: "Absolutely. We support REST API integrations to seamlessly connect MX-ERP with your existing CRM tools, third-party logistics systems, e-commerce gateways, and industrial weighing scale hardware." },
    ],
    buyersChecklist: [
      "Cross-Departmental Sync: Can it link inventory, purchase, sales, and accounts automatically?",
      "Scalable Architecture: Can you start with 10 users and expand to 500+ without changing the core platform?",
      "On-field Access: Do remote site managers and sales reps have access to secure mobile dashboards?",
      "No Hidden Costs: Are implementation, custom report building, and user training clearly defined in the quote?",
      "Data Security: Does the platform offer daily automated backups and encrypted user roles?",
    ],
    features: [
      { name: "Manufacturing ERP", desc: "Streamline production, planning & operations.", icon: "factory", href: "/products/manufacturing-erp-software" },
      { name: "Traders ERP", desc: "Efficient trading & inventory management.", icon: "shopping-bag", href: "/products/traders-erp-software" },
      { name: "Distributors ERP", desc: "Control supply chain & distribution network.", icon: "truck", href: "/products/distributors-erp-software" },
      { name: "Ecommerce ERP", desc: "From orders to delivery, everything connected.", icon: "globe", href: "/products/ecommerce-erp-software" },
      { name: "Buying House ERP", desc: "From sourcing to shipment, one system.", icon: "shirt", href: "/products/buying-house-erp-software" },
    ],
  },
  {
    id: "construction-mx-erp",
    category: "Construction & EPC",
    categoryShort: "Construction",
    product: "Mx-ERP",
    description:
      "Mx-ERP by ACGIL is a powerful Construction Management ERP Software designed to give you complete control over site planning, procurement, materials, labour, and payments, all in one unified platform.",
    heroHeadline: "Construction Management ERP Software",
    heroSubheadline:
      "Mx-ERP by ACGIL is a powerful Construction Management ERP Software designed to give you complete control over site planning, procurement, materials, labour, and payments, all in one unified platform.",
    color: "#F59E0B",
    gradient: "from-[#F59E0B] to-[#D97706]",
    gradientBg: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
    icon: "hard-hat",
    productSlug: "project-management-erp-software",
    painPoints: [
      { title: "Project Delays", desc: "Missed deadlines due to poor planning and zero real-time tracking." },
      { title: "Budget Overruns", desc: "Cost leakage and uncontrolled expenses eating into project margins." },
      { title: "Poor Resource Allocation", desc: "Workforce and equipment mismanagement leading to idle capacity." },
      { title: "Zero Visibility", desc: "No centralized view across multiple projects and teams." },
      { title: "Manual Billing", desc: "Delayed invoicing and revenue recognition due to manual processes." },
    ],
    industriesServed: [
      "EPC & Infrastructure Companies",
      "Civil & Structural Engineering",
      "IT & Software Projects",
      "Oil, Gas & Energy Projects",
      "Industrial Plant Engineering",
      "Interior Design & Fit-out",
      "Government & Public Works",
    ],
    aiFeatures: [
      {
        title: "Predictive Project Scheduling",
        description: "AI algorithms analyze historical project data to predict potential delays and automatically reschedule tasks to keep projects on track.",
        bulletPoints: [
          "Analyzes past project timelines for delay prediction",
          "Automatically adjusts schedules based on risk factors",
          "Reduces project overruns through smart planning",
        ],
      },
      {
        title: "Smart Resource Optimization",
        description: "Machine learning optimizes workforce and equipment allocation across multiple active projects in real time.",
        bulletPoints: [
          "Real-time workforce and equipment allocation",
          "Identifies idle resources across project sites",
          "Maximizes utilization and reduces project costs",
        ],
      },
      {
        title: "Automated Cost Forecasting",
        description: "ML-powered cost tracking predicts budget overruns before they happen, flagging risk items early for proactive intervention.",
        bulletPoints: [
          "Predicts budget overruns using historical cost data",
          "Flags high-risk cost items for early intervention",
          "Enables proactive cost control and budget management",
        ],
      },
    ],
    processSteps: [
      { step: "01", title: "Project Planning & Scheduling", description: "Create detailed project timelines with Gantt chart visibility, milestone tracking, and task dependency management." },
      { step: "02", title: "Resource Allocation", description: "Optimize workforce and equipment utilization across all active projects with real-time productivity monitoring." },
      { step: "03", title: "Execution & Tracking", description: "Monitor live project progress, manage material requirements, and track costs against budgets in real time." },
      { step: "04", title: "RA Billing & Invoicing", description: "Automate running account billing, milestone invoicing, and payment follow-ups for faster revenue realization." },
      { step: "05", title: "Quality & Compliance", description: "Conduct quality checks at every project stage and ensure compliance with industry standards and regulations." },
      { step: "06", title: "Project Closure & Analytics", description: "Generate final project profitability reports, capture lessons learned, and archive complete project documentation." },
    ],
    businessImpact: [
      { value: "35%", label: "Project Efficiency", description: "Improve delivery performance with optimized AI-driven scheduling", color: "#00F2FE", icon: "TrendingUp" },
      { value: "30%", label: "Fewer Overruns", description: "Reduce budget leakage with real-time cost tracking and alerts", color: "#7F77DD", icon: "TrendingDown" },
      { value: "40%", label: "More Visibility", description: "Gain complete real-time visibility across all active projects", color: "#F59E0B", icon: "Eye" },
      { value: "2X", label: "Faster Billing", description: "Accelerate billing cycles with automated RA and milestone invoicing", color: "#EC4899", icon: "Zap" },
    ],
    comparisonRows: [
      { feature: "Implementation Time", mxerp: "4–6 Weeks", others: "6–12 Months" },
      { feature: "Total Cost", mxerp: "Low", others: "High" },
      { feature: "Customization", mxerp: "High", others: "Limited" },
      { feature: "RA Billing", mxerp: "Built-in (RA/Milestone)", others: "Add-on" },
      { feature: "Industry Focus", mxerp: "Project-Based", others: "Generic" },
      { feature: "Cloud + On-Premise", mxerp: "Both Options", others: "Cloud Only" },
      { feature: "Local Support (India)", mxerp: "24/7 Dedicated", others: "Timezone Issues" },
    ],
    faqItems: [
      { q: "What is Project Management ERP Software?", a: "Project Management ERP Software is an integrated system that helps businesses manage project planning, execution, budgeting, resources, billing, and reporting in one platform." },
      { q: "Who should use Project ERP Software?", a: "Construction companies, EPC contractors, engineering firms, and any project-based organizations benefit from using project ERP software." },
      { q: "Does Mx-ERP support RA billing features?", a: "Yes, Mx-ERP supports RA billing, milestone billing, automated invoicing, and revenue tracking for construction projects." },
      { q: "Can it manage multiple projects simultaneously?", a: "Yes, Mx-ERP allows businesses to manage multiple projects with real-time tracking and centralized dashboards across all sites." },
    ],
    buyersChecklist: [
      "Multi-Project Tracking: Can it manage multiple simultaneous projects with real-time visibility?",
      "RA Billing Support: Does it natively support running account billing and milestone invoicing?",
      "Resource Management: Can it track workforce and equipment allocation across sites?",
      "Budget Control: Does it provide real-time budget vs actual tracking with overrun alerts?",
      "Mobile Access: Can site managers update progress and view dashboards from mobile devices?",
      "GST Compliance: Is e-invoicing and GST reporting built into the billing module?",
    ],
    features: [
      { name: "Project Management", desc: "Plan, execute and monitor construction projects.", icon: "briefcase", href: "/products/project-management-erp-software" },
      { name: "Tender Management", desc: "Discover tenders, estimate BOQs and win profitable bids.", icon: "clipboard-list", href: "/products/tender-management-software" },
      { name: "R.A Billing", desc: "Streamline running account billing & payments.", icon: "file-text", href: "/products/ra-billing-erp-software" },
      { name: "Contract Management", desc: "Manage vendors, sub-contracts & agreements.", icon: "handshake", href: "/products/contract-management-erp-software" },
      { name: "Quantity Surveyor", desc: "Accurate estimation & material takeoff control.", icon: "bar-chart-3", href: "/products/quantity-surveyor-erp-software" },
    ],
  },
  {
    id: "electra-his",
    category: "Healthcare & Labs",
    categoryShort: "Healthcare",
    product: "Electra-HIS",
    description:
      "Fully integrated Hospital Information System covering HMIS, LIMS, and patient care workflows.",
    heroHeadline: "Healthcare & Labs",
    heroSubheadline:
      "Fully integrated Hospital Information System covering HMIS, LIMS, and patient care workflows — all powered by Electra-HIS.",
    color: "#3B82F6",
    gradient: "from-[#3B82F6] to-[#2563EB]",
    gradientBg: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
    icon: "stethoscope",
    productSlug: "hospital-management-erp-software",
    painPoints: [
      { title: "Manual Record Errors", desc: "Patient data entry mistakes leading to care delays and compliance risks." },
      { title: "Billing Delays", desc: "Slow invoice generation and discharge processing affecting cash flow and patient experience." },
      { title: "Pharmacy Gaps", desc: "Poor inventory tracking causing stockouts, expired medicines, and revenue leakage." },
      { title: "Zero Real-Time Data", desc: "No centralized visibility into beds, staff, and operations across departments." },
      { title: "Siloed Staff Management", desc: "Inefficient scheduling and coordination impacting care quality and productivity." },
    ],
    industriesServed: [
      "Multi-Specialty Hospitals",
      "Diagnostic & Pathology Labs",
      "Pharmacies & Medical Stores",
      "Clinics & Nursing Homes",
      "Government Healthcare Facilities",
      "Telemedicine & Digital Health",
    ],
    aiFeatures: [
      {
        title: "Predictive Patient Analytics",
        description: "AI analyzes patient history and symptoms to predict health risks and recommend preventive care measures early.",
        bulletPoints: [
          "Analyzes patient history for early risk detection",
          "Recommends preventive care measures proactively",
          "Reduces readmission rates through smart insights",
        ],
      },
      {
        title: "Smart Inventory Management",
        description: "ML-powered pharmacy inventory management predicts medicine demand, automates reordering, and prevents stockouts or expiry wastage.",
        bulletPoints: [
          "Predicts medicine demand based on patient trends",
          "Automates reordering to prevent stockouts",
          "Prevents expiry wastage with smart expiry alerts",
        ],
      },
      {
        title: "Intelligent Billing Automation",
        description: "AI automates insurance claims processing, detects billing anomalies, and ensures accurate, GST-compliant invoicing in real time.",
        bulletPoints: [
          "Automates insurance and TPA claim processing",
          "Detects billing anomalies before submission",
          "Ensures accurate GST-compliant invoicing",
        ],
      },
    ],
    processSteps: [
      { step: "01", title: "Patient Registration & EMR", description: "Digitally register patients, capture complete medical history, and manage appointments with automated reminders." },
      { step: "02", title: "Clinical Assessment", description: "Record vitals, diagnoses, and prescriptions in structured EMR/EHR with decision support alerts." },
      { step: "03", title: "Pharmacy & Inventory", description: "Manage medicine stock with batch tracking, expiry alerts, and automated reordering across all store points." },
      { step: "04", title: "Diagnostics & Lab (LIMS)", description: "Track samples from collection to report generation with automated result validation and digital report delivery." },
      { step: "05", title: "Billing & Insurance", description: "Generate accurate discharge bills, process insurance/TPA claims, and handle GST-compliant e-invoicing." },
      { step: "06", title: "Analytics & Reporting", description: "Monitor hospital KPIs, revenue trends, patient outcomes, and department performance in real-time dashboards." },
    ],
    businessImpact: [
      { value: "50%", label: "Fewer Errors", description: "Reduce manual data entry errors with automated digital records", color: "#00F2FE", icon: "TrendingDown" },
      { value: "40%", label: "Faster Billing", description: "Accelerate discharge processing and invoicing workflows", color: "#7F77DD", icon: "Zap" },
      { value: "30%", label: "More Efficiency", description: "Improve hospital-wide productivity and staff coordination", color: "#F59E0B", icon: "TrendingUp" },
      { value: "99.9%", label: "Data Accuracy", description: "Ensure data integrity with role-based access and audit trails", color: "#EC4899", icon: "Shield" },
    ],
    comparisonRows: [
      { feature: "Patient Records", mxerp: "Full EMR/EHR Integrated", others: "Basic Paper/Excel Records" },
      { feature: "Billing & Insurance", mxerp: "Automated + TPA Integrated", others: "Manual Processing" },
      { feature: "Pharmacy (LIMS)", mxerp: "Built-in with Expiry Alerts", others: "External Add-on" },
      { feature: "Lab Management", mxerp: "Full LIMS Module", others: "Separate System Needed" },
      { feature: "Reports & Analytics", mxerp: "Real-time Dashboards", others: "Static Monthly Reports" },
      { feature: "GST Compliance", mxerp: "Built-in E-invoicing", others: "Manual Compliance" },
    ],
    faqItems: [
      { q: "What is Hospital Management Software (HMIS)?", a: "Hospital Management Software (HMIS) is an integrated system that helps manage patient records, appointments, billing, pharmacy, inventory, and staff — all in one platform for improved patient care." },
      { q: "Does Electra-HIS support lab management (LIMS)?", a: "Yes, Electra-HIS includes a full LIMS module for sample tracking, report generation, and result management — fully integrated with the hospital system." },
      { q: "Can it handle insurance and TPA billing?", a: "Yes, Electra-HIS supports automated insurance claim processing, TPA integration, and GST-compliant e-invoicing for seamless hospital billing." },
      { q: "Is Electra-HIS suitable for small clinics?", a: "Yes, Electra-HIS is scalable and works for clinics, nursing homes, and large multi-specialty hospitals alike." },
    ],
    buyersChecklist: [
      "EMR/EHR Support: Does it offer complete digital patient record management?",
      "LIMS Integration: Is lab management fully integrated, or a separate system?",
      "Insurance & TPA: Does it support automated insurance claim processing?",
      "Pharmacy Management: Does it handle batch tracking, expiry alerts, and auto-reordering?",
      "Mobile Access: Can doctors and staff access records from mobile devices?",
      "GST Compliance: Is e-invoicing and GST reporting built into the billing module?",
    ],
    features: [
      { name: "Hospital Management", desc: "Simplify healthcare management end-to-end.", icon: "hospital", href: "/products/hospital-management-erp-software" },
      { name: "Lab Management (LIMS)", desc: "From sample to report, fully connected.", icon: "flask-conical", href: "/products/lab-management-erp-software" },
    ],
  },
  {
    id: "mx-crm",
    category: "Customer Relationship",
    categoryShort: "CRM",
    product: "Mx-CRM",
    description:
      "Complete sales automation and lead management system to accelerate your business growth.",
    heroHeadline: "Customer Relationship",
    heroSubheadline:
      "Complete sales automation and lead management system to accelerate your business growth — all powered by Mx-CRM.",
    color: "#8B5CF6",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
    gradientBg: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
    icon: "users",
    productSlug: "sales-automation-crm-software",
    painPoints: [
      { title: "Scattered Lead Data", desc: "Leads tracked across spreadsheets, WhatsApp, and multiple tools with no unified view." },
      { title: "Missed Follow-ups", desc: "Manual follow-up tracking leads to dropped deals and lost revenue opportunities." },
      { title: "No Sales Pipeline Visibility", desc: "Inability to see deal stages, forecast revenue, or identify bottlenecks in real time." },
      { title: "Poor Customer Retention", desc: "No structured post-sale engagement, leading to customer churn and missed upsell opportunities." },
      { title: "Disconnected Teams", desc: "Sales, marketing, and support teams working in silos without shared customer context." },
    ],
    industriesServed: [
      "Real Estate & Property",
      "Financial Services & Insurance",
      "IT & Software Sales",
      "Manufacturing & Industrial Sales",
      "Healthcare & Pharma Sales",
      "Education & EdTech",
      "Retail & E-commerce",
    ],
    aiFeatures: [
      {
        title: "Predictive Lead Scoring",
        description: "AI analyzes lead behavior and engagement patterns to score and prioritize high-conversion prospects automatically.",
        bulletPoints: [
          "Analyzes lead engagement and behavior patterns",
          "Automatically scores and prioritizes hot leads",
          "Increases conversion rates through smart lead routing",
        ],
      },
      {
        title: "Intelligent Sales Forecasting",
        description: "ML models predict deal closure probability and revenue forecasts based on historical sales data and pipeline trends.",
        bulletPoints: [
          "Predicts deal closure probability in real time",
          "Generates accurate revenue forecasts from pipeline data",
          "Helps managers make informed sales strategy decisions",
        ],
      },
      {
        title: "Automated Follow-up Sequences",
        description: "AI-powered automation triggers personalized follow-up sequences based on customer interaction history and buying signals.",
        bulletPoints: [
          "Triggers personalized follow-ups based on behavior",
          "Reduces manual effort for sales representatives",
          "Ensures no lead falls through the cracks",
        ],
      },
    ],
    processSteps: [
      { step: "01", title: "Lead Capture & Enrichment", description: "Capture leads from multiple channels, auto-enrich with company data, and score them using AI for priority routing." },
      { step: "02", title: "Qualification & Nurturing", description: "Auto-qualify leads based on criteria, assign to sales reps, and trigger personalized nurture sequences." },
      { step: "03", title: "Proposal & Negotiation", description: "Generate proposals, track negotiations, manage contracts, and log all customer interactions in one timeline." },
      { step: "04", title: "Deal Closure", description: "Convert won deals to customers, trigger onboarding workflows, and hand off to account management seamlessly." },
      { step: "05", title: "Account Management", description: "Track customer health scores, manage renewals, identify upsell opportunities, and maintain post-sale engagement." },
      { step: "06", title: "Analytics & Optimization", description: "Analyze sales performance, conversion funnels, team productivity, and revenue metrics with real-time dashboards." },
    ],
    businessImpact: [
      { value: "40%", label: "More Conversions", description: "Increase lead-to-customer conversion with AI-powered lead scoring", color: "#00F2FE", icon: "TrendingUp" },
      { value: "60%", label: "Faster Follow-ups", description: "Reduce response time with automated follow-up sequences", color: "#7F77DD", icon: "Zap" },
      { value: "35%", label: "Better Retention", description: "Improve customer retention through proactive account management", color: "#F59E0B", icon: "Heart" },
      { value: "2X", label: "Pipeline Visibility", description: "Get complete real-time visibility into your sales pipeline", color: "#EC4899", icon: "Eye" },
    ],
    comparisonRows: [
      { feature: "Lead Management", mxerp: "AI-Powered Scoring & Routing", others: "Basic Contact Tracking" },
      { feature: "Sales Automation", mxerp: "Full Workflow Automation", others: "Manual Follow-up Reminders" },
      { feature: "Pipeline Visibility", mxerp: "Real-time Sales Dashboard", others: "Static Spreadsheet Reports" },
      { feature: "Integration", mxerp: "REST API + 50+ Integrations", others: "Limited Third-party Support" },
      { feature: "Mobile Access", mxerp: "Full-featured Mobile App", others: "Basic Mobile View" },
      { feature: "Support", mxerp: "Dedicated Account Manager", others: "Generic Helpdesk" },
    ],
    faqItems: [
      { q: "What is CRM Software and why do I need it?", a: "CRM (Customer Relationship Management) software helps businesses manage leads, automate follow-ups, track deals, and maintain customer relationships in one platform — driving higher conversions and retention." },
      { q: "Does Mx-CRM integrate with my existing tools?", a: "Yes, Mx-CRM offers REST API integrations and connects with 50+ tools including email, ERP, marketing automation, and communication platforms." },
      { q: "Can Mx-CRM handle my entire sales team?", a: "Yes, Mx-CRM is fully scalable. You can start with 5 users and expand to 500+ without changing the core platform." },
      { q: "Does it support sales forecasting?", a: "Absolutely. Mx-CRM uses AI-powered forecasting to predict deal closures, track pipeline health, and generate accurate revenue projections." },
    ],
    buyersChecklist: [
      "Lead Scoring: Does it use AI to automatically score and prioritize leads?",
      "Automation: Can it automate follow-ups, emails, and deal progression workflows?",
      "Pipeline Management: Does it provide real-time visibility into deal stages?",
      "Mobile Access: Is there a full-featured mobile app for sales reps on the go?",
      "Integrations: Does it integrate with your existing ERP, email, and communication tools?",
      "Reporting: Does it offer real-time dashboards for sales performance and conversion analytics?",
    ],
    features: [
      { name: "Sales Automation", desc: "Automate follow-ups and close deals faster.", icon: "bar-chart-2", href: "/products/sales-automation-crm-software" },
      { name: "Lead Management", desc: "Capture, track & convert leads efficiently.", icon: "users", href: "/products/lead-management-crm-software" },
      { name: "Service Management", desc: "Handle customer support & service requests.", icon: "headphones", href: "/products/service-management-software" },
    ],
  },
];

export function getPlatformById(id: string) {
  return platforms.find((p) => p.id === id);
}

export function getPlatformsByFamily(family: string): Platform[] {
  return platforms.filter((p) => p.product === family);
}
